import { BooleanExpr, DateInput, Field, GenericTemplatedBlock, SummaryExpr, ValueExpr } from "../survey";
import { timezoneToPrettyName } from "./expr_to_sql";

export function generateDate(expr: DateInput): number {
  const { year, month, day, hour, minute, timezone } = expr;
  let dateToReturn = Date.UTC(
    year,
    Math.max(month - 1, 0),
    day,
    hour,
    minute
  );

  if (timezone) {
    // use dateToReturn to account for different offsets during different daylight savings times.
    const localDate = new Date(dateToReturn);
    
    // calculate offset between target timezone and UTC - whatever local time is is our reference point.
    const targetTimezoneDate = new Date(localDate.toLocaleString('en-US', { timeZone: timezone }));
    const utcTimezoneDate = new Date(localDate.toLocaleString('en-US', { timeZone: 'UTC' }));
    const offsetMs = utcTimezoneDate.getTime() - targetTimezoneDate.getTime();

    dateToReturn = dateToReturn + offsetMs;
  }

  return dateToReturn;
}

export function CompileExpressionToJS(cond: BooleanExpr | ValueExpr | GenericTemplatedBlock<BooleanExpr[]>, 
  context?: { currentUser?: string, enableLastModifiedInJS?: boolean }
) {
  function generateConditions(expr: BooleanExpr | ValueExpr | GenericTemplatedBlock<BooleanExpr[]> | SummaryExpr): string {
    const dateKinds = ['Last Modified Date', 'As Date', 'Shift Date', 'Now'];
    switch (expr.kind) {
      case 'And':
        return '(' + expr.clauses.map(generateConditions).join(' &&\n ') + ')';
      case 'Or':
        return '(' + expr.clauses.map(generateConditions).join(' ||\n ') + ')';
      case 'Not': 
        return '(! ' + generateConditions(expr.clause) + ')';
      case 'Equals':
        return `info.${expr.field} === '${expr.value}'`;
      case 'Not Equal':
        return `(info.${expr.field} !== '${expr.value}')`;
      case 'Exists':
        return `(!!info.${expr.field})`;
      case 'DoesntExist':
        return `(!info.${expr.field})`;
      case 'Never':
        return 'false';
      case 'Code Boolean Expr':
        return '(' + expr.code + ')';
        // TOOD: Thread edit times into formulas
      case 'After':
      case 'Before':
        const compare = generateDate({kind: 'Date', ...expr.date, ...expr.time });

        return `Date.now() ${expr.kind === 'After' ? '>' : '<'} ${compare}`;
      case 'Last Modified':
        // Compares if field was modified within time window (ago.amount ago.unit)
        if (context?.enableLastModifiedInJS) {
          return `(() => {
            const fieldValue = info.${expr.field};
            const createdDate = info.${expr.field}_created_at ? new Date(info.${expr.field}_created_at).getTime() : null;
            if (!createdDate) return false;
            const now = Date.now();
            const ${expr.ago.unit} = ${expr.ago.amount} * ${expr.ago.unit === 'days' ? 24 * 60 * 60 * 1000 : expr.ago.unit === 'weeks' ? 7 * 24 * 60 * 60 * 1000 : 30 * 24 * 60 * 60 * 1000};
            const timeWindow = ${expr.ago.unit};
            return (now - createdDate) < timeWindow;
          })()`;
        }
        return 'false' // Feature disabled
      case 'Last Modified Date':
        if (context?.enableLastModifiedInJS) {
          return `info.${expr.field}_created_at ? new Date(info.${expr.field}_created_at) : null`;
        }
        return `undefined`; // Feature disabled
      case 'Field':
        return `info.${expr.field}`;
      case 'Value Contains':
        let toLowerCase = expr.caseInsensitive ? '.toLowerCase()' : '';
        if (expr.mode === 'comma separated list')
          return `((${generateConditions(expr.value)} || '')${toLowerCase}.split(',').includes(${generateConditions(expr.contains)}${toLowerCase}))`;
        return `((${generateConditions(expr.value)} || '')${toLowerCase}.indexOf(${generateConditions(expr.contains)}${toLowerCase}) !== -1)`;
      case 'Value Equals':
        return `(String(${generateConditions(expr.value)} || '') === String(${generateConditions(expr.equals)} || ''))`;
      case 'Value Not Equal':
        return `(String(${generateConditions(expr.value)} || '') !== String(${generateConditions(expr.notEqual)} || ''))`;
      case 'Value Empty':
        return `(!${generateConditions(expr.value)})`;
      case 'Value Not Empty':
        return `(!!${generateConditions(expr.value)})`;
      case 'Value Less Than':
        if (dateKinds.includes(expr.value.kind)) {
          return `(${generateConditions(expr.value)} < ${generateConditions(expr.lessThan)})`;
        }
        return `(parseFloat(${generateConditions(expr.value)}) < parseFloat(${generateConditions(expr.lessThan)}))`;
      case 'Value Greater Than':
        if (dateKinds.includes(expr.value.kind)) {
          return `(${generateConditions(expr.value)} > ${generateConditions(expr.greaterThan)})`;
        }
        return `(parseFloat(${generateConditions(expr.value)}) > parseFloat(${generateConditions(expr.greaterThan)}))`;
      case 'When':
        return `(() => {${(expr.when || [{
          cond: { kind: 'Never' },
          then: expr.otherwise
        }]).map((e, i) => 
          (i == 0 ? '' : 'else ') + `if (${generateConditions(e.cond)}) { return ${generateConditions(e.then)}; }`).join('\n')
        } else { return ${generateConditions(expr.otherwise)} } })()`;
      case 'Transform String':
        let s = generateConditions(expr.value);
        if (expr.transform === 'lowercase') {
          return `${s}.toLowerCase()`;
        } else if (expr.transform === 'uppercase') {
          return `${s}.toUpperCase()`;
        } else if (expr.transform === 'titlecase') {
          return `${s}.split(' ').map((word) => word[0].toUpperCase() + word.slice(1)).join(' ')`;
        } else if (expr.transform === 'trim') {
          return `${s}.trim()`;
        }
        return s;
      case 'StringValue':
        return `'${expr.value.replace("'", "\\'")}'`;
      case 'NumericValue':
      case 'BooleanValue':
        return `${expr.value}`;
      case 'Current User':
        return `'${context?.currentUser?.replace("'", "\\'") || 'No user set'}'`;
      case 'Applicant Luck':
        // TODO: thread luck into formulas
        return 'Luck not available in computations';
      case 'As Date':
        if (expr.value.kind === 'Date') {
          return `new Date(${generateDate(expr.value)})`;
        }

        const conditions = generateConditions(expr.value);

        if (expr.format) {
          const dateOpts = (() => {
            switch (expr.format.dateStyle) {
              case 'short': return "month: 'numeric', day: 'numeric', year: 'numeric'";
              case 'medium': return "month: 'short', day: 'numeric', year: 'numeric'";
              case 'long': return "month: 'long', day: 'numeric', year: 'numeric'";
              case 'full': return "weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'";
              default: return "month: 'short', day: 'numeric', year: 'numeric'";
            }
          })();
          const timeOpts = (() => {
            switch (expr.format.timeStyle) {
              case 'short': return "hour: '2-digit', minute: '2-digit', hour12: true";
              case 'long': return "hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true";
              case 'twentyfourhour': return "hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false";
              default: return "hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false";
            }
          })();
          const timeZone = expr.format.timeZone ? `, timeZone: '${expr.format.timeZone}'` : '';
          const formatOpts = `${dateOpts}, ${timeOpts}${timeZone}`;
          const tzSuffix = expr.format.timeZone
            ? ` + ' ' + '${timezoneToPrettyName(expr.format.timeZone)}'`
            : '';

          return `(() => {
            const val = ${conditions};
            if (val == null || val === '') return null;
            const d = val instanceof Date ? val : new Date(val);
            if (isNaN(d.getTime())) return null;
            return new Intl.DateTimeFormat('en-US', { ${formatOpts} }).format(d)${tzSuffix};
          })()`;
        }

        return `new Date(${conditions})`;
      case 'Shift Date':
        const generateIntervalField = (field: number | Field | undefined) => 
          (!field || typeof field === 'number') ? (field || 0) : (`+${generateConditions(field)}`);

        // Date.setMonth has undesirable behavior when a given day doesn't exist in a future month.
        // For example, if the date is Jan 31 and you add one month, you get Feb 31, which then converts to
        // March 2 or 3, depending on whether it's a leap year. We want to get Feb 28 or 29, so we have
        // to manually cap the day to the last day of the target month.
        // Convert the input value to a Date object to handle string inputs (e.g., from Field expressions)
        const dateValue = generateConditions(expr.value);
        return `((date) => {
                    if(date && !isNaN(date.getTime())) {
                      date.setFullYear(date.getFullYear() + ${generateIntervalField(expr.interval.years)});

                      const targetMonth = date.getMonth() + ${generateIntervalField(expr.interval.months)};
                      const numDaysInTargetMonth = new Date(date.getFullYear(), targetMonth + 1, 0).getDate();
                      const targetDayInTargetMonth = Math.min(date.getDate(), numDaysInTargetMonth);
                      date.setMonth(targetMonth, targetDayInTargetMonth);

                      date.setDate(date.getDate() + ${generateIntervalField(expr.interval.days)});
                      date.setHours(date.getHours() + ${generateIntervalField(expr.interval.hours)});
                      date.setMinutes(date.getMinutes() + ${generateIntervalField(expr.interval.minutes)});
                    }
                    return date;
                })(new Date(${dateValue}))`;
      case 'Now':
        return 'new Date()';
      case 'Templated Block':
        throw new Error("expressed has not been expanded");
      case 'Count':
        return ``;
      case 'Mean':
        return ``;
      case 'Median':
        return ``;
      case 'Sum':
        return ``;
      case 'Percentage':
        return `( (parseFloat(${generateConditions(expr.numerator)}) / parseFloat(${generateConditions(expr.denominator)})) * 100 )`;
      case 'Round':
        return `( Number(${generateConditions(expr.value)}).toFixed(${expr.places || 0}) )`
      case 'Add':
        if (Array.isArray(expr.secondValue)) {
          return `( parseFloat(${generateConditions(expr.baseValue)}) + ` + expr.secondValue.map(value => `parseFloat(${generateConditions(value)})`).join(' +\n ') + ')';
        } else {
          return `( parseFloat(${generateConditions(expr.baseValue)}) + parseFloat(${generateConditions(expr.secondValue)}) )`;
        }
      case 'Subtract':
        if (Array.isArray(expr.secondValue)) {
          return `( parseFloat(${generateConditions(expr.baseValue)}) - ` + expr.secondValue.map(value => `parseFloat(${generateConditions(value)})`).join(' -\n ') + ')';
        } else {
          return `( parseFloat(${generateConditions(expr.baseValue)}) - parseFloat(${generateConditions(expr.secondValue)}) )`;
        }
      case 'Multiply':
        if (Array.isArray(expr.secondValue)) {
          return `( parseFloat(${generateConditions(expr.baseValue)}) * ` + expr.secondValue.map(value => `parseFloat(${generateConditions(value)})`).join(' *\n ') + ')';
        } else {
          return `( parseFloat(${generateConditions(expr.baseValue)}) * parseFloat(${generateConditions(expr.secondValue)}) )`;
        }
      case 'Divide':
        if (Array.isArray(expr.secondValue)) {
          return `( parseFloat(${generateConditions(expr.baseValue)}) / ` + expr.secondValue.map(value => `parseFloat(${generateConditions(value)})`).join(' /\n ') + ')';
        } else {
          return `( parseFloat(${generateConditions(expr.baseValue)}) / parseFloat(${generateConditions(expr.secondValue)}) )`;
        }                  
      case 'Bucket Date':
        return `((date) => {
                        if ('${expr.bucket}' === 'week') date.setDate(date.getDate() - date.getDay() + 1);
                        switch ('${expr.bucket}') {
                            case 'year':
                                date.setMonth(0);
                            case 'month':
                                date.setDate(1);
                            case 'week':
                            case 'day':
                                date.setHours(0);
                            case 'hour':
                                date.setMinutes(0);
                            case 'minute':
                                date.setSeconds(0);
                                date.setMilliseconds(0);
                                break;
                        }
                        return date;
                    })(${generateConditions(expr.value)})`;
      case 'Sync Status':
        return `"${expr.kind}"`;
      case 'Expand':
        return `JSON.parse(${expr.value})`;
      case 'Concat':
        return expr.value.map(generateConditions).join(' + "' + (expr.delimiter === '' || expr.delimiter ? expr.delimiter : ' ') + '" +');
      case 'Extract':
        const projection = (Array.isArray(expr.path) ? expr.path : [expr.path]).join('?.');
        return `(() => {
          // prevents potential crashes due to using JSON.parse
          try {
            return JSON.parse(${typeof expr.value === 'string' ? ('info.' + expr.value) : generateConditions(expr.value)})?.${projection}
          } catch {
            return null;
          }
        })()`;
      default:
        let _: never = expr;
    }
    return '';
  }

  return generateConditions(cond);
}
